import React, { FC } from 'react';

import { FormattedMessage } from 'react-intl';

import { statFormatter } from '@nhl/formatter-utilities';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import commonMessages from '~/core/commonMessages';
import { PlayerStatAbbrev, PlayerStatNames } from '~/core/enums';

import { LandingGame, TeamSeasonStats } from '../../types';
import { ContextSubHeader } from '../playersToWatch/components/header/CompareHeader';

import SideBySideStat from './components/sideBySideStat/SideBySideStat';

import {
    HeaderTeamLogoContainer,
    HeaderName,
    TeamContainer,
    TeamStatsContainer,
    HeaderContainer,
    SubHeader,
} from './styles';

type Props = {
    game: LandingGame;
    teamSeasonStats?: TeamSeasonStats;
};

const TeamStats: FC<Props> = ({ game, teamSeasonStats }) => {
    const { awayTeam, homeTeam } = game;
    return teamSeasonStats?.awayTeam === undefined || teamSeasonStats?.homeTeam === undefined ? null : (
        <TeamStatsContainer>
            <HeaderTeamLogoContainer>
                <HeaderContainer>
                    <HeaderName role="heading" aria-level={2}>
                        <FormattedMessage
                            description="A phrase describing statistics for teams"
                            defaultMessage="Team Stats"
                        />
                    </HeaderName>
                    <SubHeader role="heading" aria-level={3}>
                        <ContextSubHeader contextDescriptor={teamSeasonStats} currentSeason={game.season} />
                    </SubHeader>
                </HeaderContainer>
                <TeamContainer>
                    <TeamDisplay
                        logoUrlOverride={awayTeam.logo}
                        team={{ triCode: awayTeam.abbrev }}
                        displayName={false}
                    />
                    <TeamDisplay
                        logoUrlOverride={homeTeam.logo}
                        team={{ triCode: homeTeam.abbrev }}
                        displayName={false}
                    />
                </TeamContainer>
            </HeaderTeamLogoContainer>
            <SideBySideStat
                game={game}
                awayLeader={teamSeasonStats.awayTeam}
                homeLeader={teamSeasonStats.homeTeam}
                accessorField="ppPctg"
                rankField="ppPctgRank"
                getDisplayValue={(stat) => statFormatter.displayValueAs('ppPctg')(stat.ppPctg)}
                title={<FormattedMessage {...commonMessages.statsDescriptions.long[PlayerStatNames.powerPlayPctg]} />}
                showPercentageSymbol
            />
            <SideBySideStat
                game={game}
                awayLeader={teamSeasonStats.awayTeam}
                homeLeader={teamSeasonStats.homeTeam}
                accessorField="pkPctg"
                rankField="pkPctgRank"
                getDisplayValue={(stat) => statFormatter.displayValueAs('pkPctg')(stat.pkPctg)}
                title={<FormattedMessage {...commonMessages.statsDescriptions.long[PlayerStatNames.penaltyKillPctg]} />}
                showPercentageSymbol
            />
            <SideBySideStat
                game={game}
                awayLeader={teamSeasonStats.awayTeam}
                homeLeader={teamSeasonStats.homeTeam}
                accessorField="faceoffWinningPctg"
                rankField="faceoffWinningPctgRank"
                getDisplayValue={(stat) => statFormatter.displayValueAs('faceoffPctg')(stat.faceoffWinningPctg)}
                title={<FormattedMessage {...commonMessages.statsDescriptions.long[PlayerStatNames.faceoffPctg]} />}
                showPercentageSymbol
            />
            <SideBySideStat
                game={game}
                awayLeader={teamSeasonStats.awayTeam}
                homeLeader={teamSeasonStats.homeTeam}
                accessorField="goalsForPerGamePlayed"
                rankField="goalsForPerGamePlayedRank"
                getDisplayValue={(stat) => statFormatter.displayValueAs('goalsForPerGame')(stat.goalsForPerGamePlayed)}
                title={
                    <FormattedMessage
                        {...commonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.goalsForPerGamesPlayed]}
                    />
                }
            />
            <SideBySideStat
                game={game}
                awayLeader={teamSeasonStats.awayTeam}
                homeLeader={teamSeasonStats.homeTeam}
                accessorField="goalsAgainstPerGamePlayed"
                rankField="goalsAgainstPerGamePlayedRank"
                getDisplayValue={(stat) =>
                    statFormatter.displayValueAs('goalsAgainstPerGame')(stat.goalsAgainstPerGamePlayed)
                }
                getProgressBarValue={(val1, val2) => val2}
                title={
                    <FormattedMessage
                        {...commonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.goalsAgainstPerGamesPlayed]}
                    />
                }
            />
        </TeamStatsContainer>
    );
};

export default TeamStats;
